"use client";

import React, { useState, useEffect, useRef, useCallback } from "react";
import Link from "@/components/Link";
import Container from "@/components/Container";
import type { IHeaderBannerObject } from "@/tools/sanity/schema/objects/headerBanner";
import BellIcon from "@/assets/icons/phosphor/bellRingingOutline.svg";
import CloseIcon from "@/assets/icons/phosphor/close.svg";
import { STORAGE_KEY } from "./dismissed";
import styles from "./styles.module.scss";

interface HeaderBannerProps {
  headerBanner?: IHeaderBannerObject;
}

const HeaderBanner = ({ headerBanner }: HeaderBannerProps) => {
  // Starts from the CMS flag, not `false`.
  //
  // Rendering nothing on the server and revealing the banner after hydration
  // pushed <main> down on every page load — measured as the entire 0.118 CLS on
  // the homepage, the whole of that metric's failure. It now ships in the server
  // HTML at its final height and nothing moves.
  const [isVisible, setIsVisible] = useState(Boolean(headerBanner?.enabled));
  const bannerRef = useRef<HTMLElement>(null);

  const updateBannerHeight = useCallback(() => {
    const height = bannerRef.current?.offsetHeight || 0;
    document.documentElement.style.setProperty("--banner-height", `${height}px`);
  }, []);

  // Track banner height and update CSS variable
  useEffect(() => {
    if (isVisible) {
      updateBannerHeight();
      window.addEventListener("resize", updateBannerHeight);
      return () => window.removeEventListener("resize", updateBannerHeight);
    }
    document.documentElement.style.setProperty("--banner-height", "0px");
  }, [isVisible, updateBannerHeight]);

  useEffect(() => {
    // Only ever hides. The banner is already rendered server-side when enabled;
    // this reconciles React with what `HideDismissedBannerScript` has already
    // done for a supporter who dismissed it earlier in the session.
    if (sessionStorage.getItem(STORAGE_KEY)) {
      setIsVisible(false);
    }
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    sessionStorage.setItem(STORAGE_KEY, "true");
    document.documentElement.dataset.bannerClosed = "true";
  };

  if (!isVisible || !headerBanner?.enabled) {
    return null;
  }

  const { bannerType, mainCopy, addLink, linkText, link } = headerBanner;

  // A named region. The banner sits above <header>, outside every other
  // landmark, and content outside a landmark is skipped by anyone moving
  // through the page by landmark.
  return (
    <section
      ref={bannerRef}
      className={styles.banner}
      data-banner-type={bannerType}
      data-id="header-banner"
      aria-label="Site announcement"
    >
      <Container className={styles.container}>
        <div className={styles.content}>
          <div className={styles.message}>
            <BellIcon className={styles.icon} />
            <span className={styles.label}>CRITICAL ALERT</span>
            <div className={styles.textContainer}>
              <span className={styles.copy}>{mainCopy}</span>
              {addLink && link && linkText && (
                <Link
                  {...link}
                  className={styles.mobileLink}
                  icon="arrowRight"
                  iconColor="white"
                  iconSize="lg"
                  iconHover
                >
                  <span>{linkText}</span>
                </Link>
              )}
            </div>
            <Link
              {...link}
              className={styles.link}
              icon="arrowRight"
              iconColor="white"
              iconSize="lg"
              iconHover
            >
              <span>{linkText}</span>
            </Link>
          </div>
        </div>

        <button
          type="button"
          onClick={handleClose}
          className={styles.closeButton}
          aria-label="Close banner"
        >
          <CloseIcon />
        </button>
      </Container>
    </section>
  );
};

export default HeaderBanner;
